<template>
  <v-container class="h-full">
    <div class="flex justify-between items-center px-4 py-4">
      <div class="flex gap-4 items-center" />
      <v-text-field
        v-model="search"
        label="Search"
        prepend-inner-icon="mdi-magnify"
        class="max-w-sm"
        clearable
        density="compact"
      />
    </div>
    <v-data-table-server
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      v-model:sort-by="sortBy"
      :headers="headers"
      :items="items"
      :items-length="itemsLength"
      :items-per-page-options="[10, 25, 50, 100]"
      :loading="loading"
      :search="search"
    >
      <template #item.key="{ item }">
        <p class="pt-2 font-bold text-base" v-text="item.key" />
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex justify-center ga-2">
          <v-btn
            v-tooltip="'Download Tarball'"
            variant="flat"
            size="32"
            color="primary"
            icon="mdi-cloud-download"
            :disabled="downloadLoading === item.key"
            :loading="downloadLoading === item.key"
            @click="downloadTarball(item.key)"
          />
          <v-btn
            v-tooltip="'Import Tarball'"
            variant="flat"
            size="32"
            color="primary"
            icon="mdi-briefcase-upload"
            :disabled="importPreparing === item.key"
            :loading="importPreparing === item.key"
            @click="openImportDialog(item.key)"
          />
        </div>
      </template>
    </v-data-table-server>
    <v-dialog v-model="importDialog" max-width="720">
      <PolicyImportCard
        :tarball-file="importTarballFile"
        :lock-file="true"
        title="Policy Import"
        @imported="handleImportComplete"
      />
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { computed, defineComponent, markRaw, onMounted, ref, shallowRef, watch } from 'vue'

import PolicyImportCard from '@/components/PolicyImportCard.vue'
import { useApiStore } from '@/stores/api'

import { bytesToObjectURL } from '@/utils/api'

import type { DataTableHeader } from 'vuetify'
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.mjs'

export default defineComponent({
  name: 'TarballList',

  components: {
    PolicyImportCard,
  },

  setup () {
    const apiStore = useApiStore()
    const tarballs = shallowRef<string[]>([])
    const loading = ref(true)
    async function loadTarballs (): Promise<void> {
      loading.value = true
      try {
        const response = await apiStore.listTarballs({})
        tarballs.value = response.keys
      } catch (err) {
        window.alert(`Failed to load tarballs: ${(err as Error).message}`)
      } finally {
        loading.value = false
      }
    }
    onMounted(() => {
      loadTarballs()
    })

    const downloadLoading = ref<string | null>(null)
    async function downloadTarball (key: string): Promise<void> {
      downloadLoading.value = key
      try {
        const response = await apiStore.readTarball({ key })
        const dataUrl = bytesToObjectURL(response.tarball, 'application/gzip')
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `tarball-${key}.tar.gz`
        link.click()
        URL.revokeObjectURL(dataUrl)
      } catch (err) {
        window.alert(`Failed to download tarball: ${(err as Error).message}`)
      } finally {
        downloadLoading.value = null
      }
    }

    const importDialog = ref(false)
    const importTarballFile = ref<File | null>(null)
    const importPreparing = ref<string | null>(null)
    async function openImportDialog (key: string): Promise<void> {
      importPreparing.value = key
      try {
        const response = await apiStore.readTarball({ key })
        const filename = `tarball-${key}.tar.gz`
        importTarballFile.value = new File(
          [new Uint8Array(response.tarball)],
          filename,
          { type: 'application/gzip' },
        )
        importDialog.value = true
      } catch (err) {
        window.alert(`Failed to prepare import: ${(err as Error).message}`)
      } finally {
        importPreparing.value = null
      }
    }
    function handleImportComplete (): void {
      importDialog.value = false
    }
    watch(importDialog, (isOpen) => {
      if (!isOpen) {
        importTarballFile.value = null
      }
    })

    const headers: DataTableHeader[] = markRaw([
      { title: 'Key', key: 'key', value: 'key', sortable: true },
      { title: 'Actions', key: 'actions', value: 'actions', sortable: false, align: 'center' },
    ])
    const search = ref('')
    watch(search, () => {
      page.value = 1
    })
    const itemsPerPage = ref(10)
    const page = ref(1)
    const sortBy = ref<SortItem[]>([])
    const filteredItems = computed(() => {
      if (!search.value) return tarballs.value
      return tarballs.value.filter((key) =>
        key.toLowerCase().includes(search.value.toLowerCase()),
      )
    })
    const itemsLength = computed(() => filteredItems.value.length)
    const sortedItems = computed(() => {
      return filteredItems.value.slice().sort((a, b) => {
        for (const sortItem of sortBy.value) {
          const aValue = String(a)
          const bValue = String(b)
          if (aValue < bValue) return sortItem.order === 'desc' ? 1 : -1
          if (aValue > bValue) return sortItem.order === 'desc' ? -1 : 1
        }
        return 0
      })
    })
    const items = computed(() => {
      const offset = (page.value - 1) * itemsPerPage.value
      return sortedItems.value.slice(offset, offset + itemsPerPage.value)
        .map((key) => ({ key }))
    })

    return {
      // data
      loading,
      downloadLoading,
      importDialog,
      importTarballFile,
      importPreparing,
      // actions
      loadTarballs,
      downloadTarball,
      openImportDialog,
      handleImportComplete,
      // table
      headers,
      search,
      itemsPerPage,
      page,
      sortBy,
      itemsLength,
      items,
    }
  },
})
</script>
